import type { Address } from './address.js';
import { VendorType, VendorStatus, PaymentMethod, VendorExpenseScope, TargetAccountStatus } from '../enums.js';

export type { VendorType };

export interface VendorContactEntry {
  type: string;
  value: string;
}

export const VENDOR_TYPE_LABELS: Record<VendorType, string> = {
  [VendorType.Individual]: 'Individual',
  [VendorType.SoleProprietor]: 'Sole Proprietor/Unincorporated Business',
  [VendorType.Incorporated]: 'Incorporated (Registered) Company',
  [VendorType.NonProfit]: 'Non-Profit Organization',
};

export interface VendorAccount {
  id: string;
  name: string;
}

export interface Vendor {
  id: string;
  name: string;
  /** Legal name. Defaults to (and follows) `name` until set to something else. */
  legal_name: string;
  addresses: Address[];
  emails: VendorContactEntry[];
  phones: VendorContactEntry[];
  w9_on_file: boolean;
  ss_last_4: string | null;
  vendor_type: VendorType | null;
  is_international: boolean;
  /** Whether expenses paid to this vendor require an invoice attached */
  required_invoice: boolean;
  default_account_id: string | null;
  print_on_check_as: string | null;
  company: string | null;
  first_name: string | null;
  middle_initial: string | null;
  last_name: string | null;
  payment_method: PaymentMethod | null;
  bank_code: string | null;
  bank_account_number: string | null;
  paypal_email: string | null;
  payment_link: string | null;
  is_check_pickup: boolean;
  status: VendorStatus;
  notes: string | null;
  accounts: string[];
  accounts_resolved: VendorAccount[];
  login_enabled: boolean;
  /** When true, this vendor may enroll bank details for ACH/wire. */
  enrollment_enabled: boolean;
  /** Active means enrolled. Null until they finish enrollment. */
  target_account_status?: TargetAccountStatus | null;
  login_email: string | null;
  /** Which target_account a portal-login vendor may select on expenses (default self-only) */
  expense_scope: VendorExpenseScope;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  created_by_name?: string;
  updated_by_name?: string;
  // Aggregated in the list view — total of linked, non-rejected expense amounts.
  total_expense?: number | string;
}

/**
 * A slim vendor for the AI vendor matcher (GET /vendors?view=candidates): the
 * mailing (else first) address and the email values, without the full record.
 */
export interface VendorCandidate {
  id: string;
  name: string;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  emails: string[];
}

/** Vendor-type filter value that matches vendors with no type set (NULL). */
export const VENDOR_TYPE_NONE_FILTER = '__none__';

/** Columns the paged vendor list can be sorted by (`sort_key` on GET /vendors). */
export const VENDOR_SORT_KEYS = ['name', 'location', 'payment_method', 'total_expense', 'status'] as const;
export type VendorSortKey = (typeof VENDOR_SORT_KEYS)[number];

/** One page of GET /vendors?page=… — the vendor list page's server-side paging. */
export interface VendorListPage {
  rows: Vendor[];
  /** Vendors matching the filters across all pages. */
  total: number;
  page: number;
  page_size: number;
}

export interface CreateVendorInput {
  name: string;
  /** Blank → the vendor name. */
  legal_name?: string | null;
  addresses?: Address[];
  emails?: VendorContactEntry[];
  phones?: VendorContactEntry[];
  w9_on_file?: boolean;
  ss_last_4?: string;
  vendor_type?: VendorType | null;
  is_international?: boolean;
  required_invoice?: boolean;
  default_account_id?: string | null;
  print_on_check_as?: string | null;
  company?: string | null;
  first_name?: string | null;
  middle_initial?: string | null;
  last_name?: string | null;
  payment_method?: PaymentMethod;
  bank_code?: string;
  bank_account_number?: string;
  paypal_email?: string;
  payment_link?: string;
  is_check_pickup?: boolean;
  notes?: string;
  accounts?: string[];
}

export interface UpdateVendorInput {
  name?: string;
  /** Blank → the vendor name. */
  legal_name?: string | null;
  addresses?: Address[];
  emails?: VendorContactEntry[];
  phones?: VendorContactEntry[];
  w9_on_file?: boolean;
  ss_last_4?: string | null;
  vendor_type?: VendorType | null;
  is_international?: boolean;
  required_invoice?: boolean;
  default_account_id?: string | null;
  print_on_check_as?: string | null;
  company?: string | null;
  first_name?: string | null;
  middle_initial?: string | null;
  last_name?: string | null;
  payment_method?: PaymentMethod | null;
  bank_code?: string | null;
  bank_account_number?: string | null;
  paypal_email?: string | null;
  payment_link?: string | null;
  is_check_pickup?: boolean;
  status?: VendorStatus;
  notes?: string | null;
  accounts?: string[];
  login_enabled?: boolean;
  login_email?: string | null;
  expense_scope?: VendorExpenseScope;
}

export interface BulkCreateVendorInput {
  vendors: CreateVendorInput[];
}

/**
 * Merge one vendor into another. All records referencing the source vendor
 * (expenses, subscriptions, documents, CC merchant aliases, role assignments)
 * are re-pointed at the target vendor, and the source vendor is then
 * deactivated. The target vendor is the surviving record.
 */
export interface MergeVendorInput {
  /** Vendor whose data is transferred away and which is then inactivated (vendor B). */
  source_vendor_id: string;
  /** Surviving vendor that receives all of the source vendor's data (vendor A). */
  target_vendor_id: string;
  /**
   * Required when the survivor is not enrolled and the source still has
   * remittance expenses waiting to be paid. Those expenses move to this method.
   * Paid remittances stay remittance. Ignored when the survivor is enrolled.
   */
  payment_method?: PaymentMethod;
}

/** Summary of what a vendor merge moved, returned by POST /vendors/merge. */
export interface MergeVendorResult {
  source_vendor_id: string;
  source_vendor_name: string;
  target_vendor_id: string;
  target_vendor_name: string;
  moved_expenses: number;
  moved_subscriptions: number;
  moved_documents: number;
  moved_cc_aliases: number;
  moved_role_assignments: number;
  /** Source vendor’s target_account was unenrolled. We do not move it onto the survivor. */
  unenrolled_target_account: boolean;
}

export interface VendorAuditEntry {
  id: string;
  vendor_id: string;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string;
  changed_at: string;
  changed_by_name?: string;
}

export interface VendorDocument {
  id: string;
  vendor_id: string;
  /** @deprecated legacy field — the documents API returns `document_type_id` / `document_type_label`. */
  document_type: string;
  document_type_id?: string | null;
  document_type_label?: string | null;
  s3_key: string | null;
  external_url: string | null;
  filename: string;
  content_type: string | null;
  uploaded_by: string;
  notes: string | null;
  created_at: string;
  uploaded_by_name?: string;
}

export interface CreateVendorDocumentInput {
  document_type: string;
  filename: string;
  contentType: string;
  notes?: string;
}
